import { FC, useEffect, useState } from 'react';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { PlaceholderBar } from '../../../../components/loading-skeleton/index';
import { ModalWrapper } from '../../../../components/modal-wrapper/index';
import { useLocale, useReactForm } from '../../../../hooks/index';
import { DataRoomModalType } from '../../enums/data-room-modal-type.enum';
import { usePatchWorkspace } from '../../hooks/use-patch-workspace.hook';
import { useWorkspace } from '../../hooks/use-workspace.hook';
import { DataRoomModalStore } from '../../store/data-room-modal.store';
import {
  CreateWorkspaceForm,
  createWorkspaceSchema,
  toCreateWorkspacePayload,
} from '../../validations/create-workspace.validation';
import { keepFocusInsideDialog } from './keep-focus-inside-dialog.util';
import { SpaceDetailsFields } from './space-details-fields';

const EMPTY_DETAILS: CreateWorkspaceForm = { description: '', name: '' };

// Same heights as the fields, so the modal keeps its size when the workspace arrives.
const SpaceDetailsFieldsPlaceholder: FC = () => (
  <div className="flex animate-pulse flex-col gap-4">
    <PlaceholderBar className="h-14 w-full rounded-t bg-gray-50" />
    <PlaceholderBar className="h-[104px] w-full rounded-t bg-gray-50" />
  </div>
);

export const WorkspaceSettingsModal: FC = () => {
  const isOpen = DataRoomModalStore(({ isOpen }) => isOpen);
  const type = DataRoomModalStore(({ type }) => type);
  const workspaceId = DataRoomModalStore(({ metadata }) => metadata?.spaceId);
  const handleAccept = DataRoomModalStore(({ handleAccept }) => handleAccept);
  const handleDismiss = DataRoomModalStore(({ handleDismiss }) => handleDismiss);
  const openModal = DataRoomModalStore(({ openModal }) => openModal);
  const { messagesLocale } = useLocale();
  const { workspace } = useWorkspace(workspaceId);
  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
    reset,
    setError,
  } = useReactForm({ defaultValues: EMPTY_DETAILS, schema: createWorkspaceSchema });
  const { isPending, patchWorkspace } = usePatchWorkspace({
    onDuplicateName: () =>
      setError('name', { message: messagesLocale['WORKSPACE_NAME_ALREADY_EXISTS'] }),
    onSuccess: handleAccept,
  });

  const [seededId, setSeededId] = useState<string>();

  // Seeded once per opening: a background refetch must not overwrite what is being typed.
  useEffect(() => {
    if (!workspace || workspace.id === seededId) return;

    setSeededId(workspace.id);
    reset({ description: workspace.description ?? '', name: workspace.name });
  }, [reset, seededId, workspace]);

  // The modal stays mounted between openings; unsaved edits must not survive a Cancel.
  useEffect(() => {
    if (isOpen) return;

    setSeededId(undefined);
    reset(EMPTY_DETAILS);
  }, [isOpen, reset]);

  if (type !== DataRoomModalType.WORKSPACE_SETTINGS || !workspaceId) {
    return null;
  }

  // A pending save cannot be dismissed, so a late response never closes a different modal.
  const dismiss = () => {
    if (!isPending) handleDismiss();
  };
  const canSave = !!workspace && isDirty && isValid && !isPending;

  const save = () => {
    void handleSubmit((values) =>
      patchWorkspace({ id: workspaceId, ...toCreateWorkspacePayload(values) }),
    )();
  };

  return (
    <ModalWrapper
      className="w-full max-w-[493px] shadow-none"
      control={{ onOpenChange: (open) => !open && dismiss(), open: isOpen }}
      onBgClick={dismiss}
      onOpenAutoFocus={keepFocusInsideDialog}
      side="CENTER"
    >
      {/* A form, so that Enter in the field saves as the button does, and never while it cannot. */}
      <form
        className="flex w-full flex-col gap-8 overflow-hidden rounded bg-white p-4 shadow-3xl"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();

          if (canSave) save();
        }}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col border-b border-gray-100 pb-5 pt-1">
            <h2 className="text-xl font-semibold leading-8 text-gray-700">
              <AppFormattedMessage id="WORKSPACE_SETTINGS" />
            </h2>
            <p className="truncate text-label-sm font-medium text-gray-500">
              {workspace?.name ?? ''}
            </p>
          </div>
          {workspace ? (
            <SpaceDetailsFields
              control={control}
              descriptionLabel="ENTER_WORKSPACE_DESCRIPTION"
              nameLabel="INTERNAL_WORKSPACE_NAME"
            />
          ) : (
            <SpaceDetailsFieldsPlaceholder />
          )}
          <button
            className="w-fit px-4 py-2 text-sm font-[550] leading-5 text-fireside-600 disabled:opacity-40"
            disabled={!workspace}
            onClick={() =>
              openModal({
                metadata: { spaceId: workspaceId },
                type: DataRoomModalType.DELETE_WORKSPACE,
              })
            }
            type="button"
          >
            <AppFormattedMessage id="DELETE_INTERNAL_WORKSPACE" />
          </button>
        </div>

        <div className="flex items-center justify-end gap-3">
          <Button
            className="h-11 w-fit border border-gray-300 px-6 text-sm font-[450] text-gray-700"
            disabled={isPending}
            onClick={dismiss}
            styleType="DEFAULT_ROUNDED"
          >
            <AppFormattedMessage id="CANCEL" />
          </Button>
          <Button
            className="h-11 w-fit px-6"
            disabled={!canSave}
            isLoading={isPending}
            type="submit"
          >
            <AppFormattedMessage id="SAVE" />
          </Button>
        </div>
      </form>
    </ModalWrapper>
  );
};
